import { Product } from '@/domain/products/types';
import { visibleTabs, when } from '@/features/products/utils/tabs';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Tab, Tabs } from '@andritzot/metris-web-ui/navigation/tabs';
import { Card, CardContent } from '@andritzot/metris-web-ui/surfaces/card';
import { useState } from 'react';

import VideoSection from '../../VideoSection';
import { FaqContent } from './FaqContent';
import { FeatureContent } from './FeatureContent';
import { KpiContent } from './KpiContent';
import { TrainingContent } from './TrainingContent';

interface OverviewTabsWithAccordionProps {
  product: Product;
  isLoggedIn: boolean;
}

const buildTabs = (product: Product, isLoggedIn: boolean) =>
  visibleTabs([
    when(product.productFaq.length, { label: 'FAQ', content: <FaqContent productFaq={product.productFaq} /> }),
    when(product.productKpis.length, { label: 'KPIS', content: <KpiContent productKpi={product.productKpis} /> }),
    when(product.productFeatures.length, {
      label: 'FEATURES',
      content: <FeatureContent productFeature={product.productFeatures} />,
    }),
    when(product.videos.length || product.streamVideos.length, {
      label: 'VIDEOS',
      content: <VideoSection videos={product.videos} streamVideos={product.streamVideos} />,
    }),
    when(isLoggedIn && product.trainings?.length > 0, {
      label: 'TRAININGS',
      content: <TrainingContent productTrainings={product.trainings} />,
    }),
  ]);

export const OverviewTabsWithAccordion = ({ product, isLoggedIn }: OverviewTabsWithAccordionProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabs = buildTabs(product, isLoggedIn);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Card sx={{ my: 4, backgroundColor: '#fff', width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => setActiveIndex(value)}
          variant="scrollable"
          scrollButtons="auto"
          type="secondary"
        >
          {tabs.map(({ label }, index) => (
            <Tab key={label} label={label} value={index} />
          ))}
        </Tabs>
      </Box>
      <CardContent>
        <Box role="tabpanel">{tabs[activeIndex]?.content}</Box>
      </CardContent>
    </Card>
  );
};
